import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-[4px] text-[14px] font-normal transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 uppercase tracking-[0.5px] font-sans cursor-pointer",
  {
    variants: {
      variant: {
        default:
          "bg-indigo text-white hover:-translate-y-[1px] hover:brightness-110 ease-out duration-200",
        void:
          "bg-charcoal text-white hover:-translate-y-[1px] hover:brightness-125 ease-out duration-200",
        signal:
          "bg-terra text-white hover:-translate-y-[1px] hover:brightness-110 ease-out duration-200",
        lime:
          "bg-terra-lt text-charcoal hover:-translate-y-[1px] hover:brightness-105 ease-out duration-200",
        white:
          "bg-cream text-charcoal border border-charcoal/10 hover:-translate-y-[1px] hover:border-charcoal/20 hover:shadow-sm ease-out duration-200",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-transparent shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-transparent border-2 border-current hover:bg-current/10 font-medium",
        ghost: "bg-transparent text-indigo hover:underline hover:underline-offset-4 font-medium p-0 h-auto hover:bg-transparent",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-auto py-4 px-8", // 16px 32px
        sm: "h-auto py-3 px-5 text-[12px]", // 12px 20px
        lg: "h-auto py-5 px-10 text-[16px]", // 20px 40px
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
